/**
 * Copyright 2024 ElectricSQL and contributors.
 * Copyright 2026 Improve Labs.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * The leader-election MESSAGE VOCABULARY (`leader-here` / `tab-here` /
 * `rpc-call` / `rpc-return` / `rpc-error`, the per-tab BroadcastChannel, and the
 * tab-close Web Lock as a liveness signal) is derived from PGlite v0.5.4
 * (commit 6787212), `packages/pglite/src/worker/index.ts` — in particular
 * `#leaderNotifyLoop` (lines 198-206) and `#rpc` (lines 208-253). The
 * The implementation below is original. `LeaderChangedError`'s name and message come
 * from PGlite lines 738-742 (see `leader-resource-errors.ts`).
 *
 * DELIBERATE DIVERGENCES from PGlite:
 *   - Requests are QUEUED during a handover and flushed in order once a new
 *     leader has SUCCESSFULLY OPENED the resource. PGlite has no queue.
 *   - The announce loop backs off (16ms for the first ~500ms, then 250ms) instead
 *     of a permanent 16ms interval, so a leader that never appears cannot turn
 *     into a broadcast storm.
 *   - Every `rpc-call` is epoch-stamped, so the leader can drop a stale call that
 *     survived a handover in a channel buffer.
 *   - `ready()` is bounded and fails on a terminal failure: a worker that dies
 *     while starting (an `error` event before `host-ready`), a worker that gave
 *     up for good, or no leader within the deadline. PGlite's equivalent waits
 *     forever.
 *
 * WEB-ONLY: throws on a runtime lacking `navigator.locks` / `BroadcastChannel`.
 */
import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";
import { acquireWebLock } from "#acquire-web-lock";
import {
  createLeaderRequestQueue,
  DEFAULT_HANDOVER_BUDGET_MS,
  type LeaderRequestQueue,
  MAX_REQUEST_TIMEOUT_MS,
} from "#leader-request-queue";
import { LeaderUnavailableError } from "#leader-resource-errors";
import { getBroadcastChannelId, getTabChannelId, getTabCloseLockId } from "#leader-resource-ids";
import {
  deserializeLeaderError,
  isConnectedMessage,
  isHostReadyMessage,
  isLeaderEventMessage,
  isLeaderFailedDirectMessage,
  isLeaderHereMessage,
  isLeaderNowMessage,
  isLeaderPausedMessage,
  isLeaderResumedMessage,
  isRelinquishedMessage,
  isRpcErrorMessage,
  isRpcReturnMessage,
  isWorkerHereMessage,
  parseLeaderMessage,
  readRequestDeadlinesMs,
  type LeaderBroadcastMessage,
  type LeaderTabMessage,
} from "#leader-resource-protocol";
import type {
  LeaderResourceApi,
  LeaderResourceClient,
  LeaderResourceClientOptions,
  LeaderResourceStatus,
} from "#leader-resource-types";

/** Aggressive at first (a leader usually already exists), then backed off. */
const ANNOUNCE_FAST_INTERVAL_MS = 16;
const ANNOUNCE_SLOW_INTERVAL_MS = 250;
const ANNOUNCE_FAST_WINDOW_MS = 500;

/**
 * How long `close()` waits for its worker to confirm the relinquish before
 * letting the caller terminate it. Long enough for a WAL checkpoint on a small
 * local database, short enough that a dead or non-leader worker (which will
 * never answer) does not visibly stall teardown.
 */
const RELINQUISH_DRAIN_TIMEOUT_MS = 2000;

/**
 * Extra time `ready()` allows beyond the handover budget: the worker has to
 * boot, evaluate its bundle and win (or lose) the election before the budget's
 * work — a leader opening the resource — even begins.
 */
const READY_GRACE_MS = 10_000;

/**
 * The tab-close lock has a unique per-tab name, so nothing else ever holds it and
 * it is granted at once. Announcing waits for it; if it never lands the tab can
 * never be adopted, so `ready()` fails instead of waiting out its full deadline.
 */
const TAB_CLOSE_LOCK_DEADLINE_MS = 10_000;

/**
 * How much LONGER than the leader's own per-method deadline this tab waits for a
 * response. The leader answers every call within its deadline — with a result or
 * its own timeout — so the tab only gives up when the leader could not answer at
 * all (it died, the channel dropped the reply), never while a write it reports as
 * failed may still land.
 */
const LEADER_RESPONSE_MARGIN_MS = 10_000;

function describeWorkerError(event: Event): string {
  if (typeof ErrorEvent !== "undefined" && event instanceof ErrorEvent) {
    const where = event.filename === "" ? "" : ` (${event.filename}:${event.lineno})`;
    return `${event.message}${where}`;
  }
  return event.type;
}

function createTabId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createLeaderResourceClient<Api extends LeaderResourceApi>(
  options: LeaderResourceClientOptions<Api>,
): LeaderResourceClient<Api> {
  if (typeof BroadcastChannel === "undefined") {
    throw new TypeError(
      "leader-resource is web-only: BroadcastChannel is unavailable in this runtime",
    );
  }

  const tabId = createTabId();
  const broadcastChannel = new BroadcastChannel(getBroadcastChannelId(options.resourceId));
  const tabChannel = new BroadcastChannel(getTabChannelId(tabId));

  let status: LeaderResourceStatus = { state: "electing" };
  let isLeader = false;
  let leaderEpoch = 0;
  let closed = false;
  let nextCallSeq = 0;
  const resumeMethods = new Set<string>(options.pauseControl?.resumeMethods);

  // Per-method budgets advertised by the current leader on `connected`.
  let leaderRequestDeadlinesMs: Record<string, number> = {};
  // The worker reported `host-ready`, i.e. its script evaluated and the host runs.
  let hostReady = false;
  // This tab's own worker will never answer again (it failed to start, gave up for
  // good, or shut itself down), so `close()` need not wait for its relinquish.
  let hostGone = false;

  let readyResolve: (() => void) | null = null;
  let readyReject: ((error: Error) => void) | null = null;
  // Bounded: the handover budget (a leader must open within it for any queued
  // call to succeed anyway) plus the worker's own boot. Before this, a worker that
  // died while starting — it happened with "process is not defined" — left
  // `ready()`, and the whole database open above it, pending forever.
  const readyPromise = boundedPromise<void>(
    {
      deadline: (options.handoverBudgetMs ?? DEFAULT_HANDOVER_BUDGET_MS) + READY_GRACE_MS,
      operation: `leader resource "${options.resourceId}" ready`,
    },
    (resolve, reject) => {
      readyResolve = resolve;
      readyReject = reject;
    },
  );
  // A `ready()` nobody awaits must not become an unhandled rejection.
  readyPromise.catch((error: unknown) => {
    if (!closed && !queue.connected && status.state !== "failed") {
      setStatus({
        reason: error instanceof Error ? error.message : String(error),
        state: "failed",
      });
    }
  });

  function setStatus(next: LeaderResourceStatus): void {
    status = next;
    options.onStatusChange?.(next);
  }

  /** This tab cannot get a leader through its own worker: fail `ready()` now. */
  function failReady(reason: string): void {
    if (queue.connected) {
      return;
    }
    setStatus({ reason, state: "failed" });
    readyReject?.(new LeaderUnavailableError(reason));
  }

  const queue: LeaderRequestQueue = createLeaderRequestQueue({
    dispatch: (entry) => {
      const call: LeaderTabMessage = {
        args: entry.args,
        callId: entry.callId,
        leaderEpoch,
        method: entry.method,
        type: "rpc-call",
      };
      tabChannel.postMessage(call);
    },
    handoverBudgetMs: options.handoverBudgetMs,
    isResendable: options.isResendable,
    now: () => Date.now(),
    onQueueChange: (queuedCount) => {
      if (status.state === "handover") {
        setStatus({ queuedCount, sinceMs: queue.sinceHandoverMs ?? 0, state: "handover" });
      }
    },
    requestTimeoutMs: options.requestTimeoutMs,
    requestTimeoutMsFor: (method) => {
      const leaderMs = leaderRequestDeadlinesMs[method];
      return leaderMs === undefined
        ? undefined
        : Math.min(leaderMs + LEADER_RESPONSE_MARGIN_MS, MAX_REQUEST_TIMEOUT_MS);
    },
    setTimer: (fn, ms) => {
      const id = setTimeout(fn, ms);
      return () => {
        clearTimeout(id);
      };
    },
  });

  // ── Announce loop ────────────────────────────────────────────────────────────
  let announceTimer: ReturnType<typeof setTimeout> | null = null;
  let announceStartedAt = 0;
  // The tab-close lock gates announcing; see `startAnnounceLoop`.
  let tabCloseLockHeld = false;
  let announceRequestedBeforeLock = false;

  function stopAnnounceLoop(): void {
    if (announceTimer !== null) {
      clearTimeout(announceTimer);
      announceTimer = null;
    }
  }

  function startAnnounceLoop(): void {
    if (announceTimer !== null || closed) {
      return;
    }
    // HARD PRECONDITION: never announce before the tab-close lock is held.
    // The leader `request()`s that lock and treats it RESOLVING as "this tab is
    // gone". If we announced first, the leader would take the still-free lock
    // immediately, conclude we had already died, and un-register us the instant
    // it registered us — leaving this tab connected to nothing while believing
    // otherwise. `announceWhenLockHeld` re-enters here once the lock lands.
    if (!tabCloseLockHeld) {
      announceRequestedBeforeLock = true;
      return;
    }
    announceStartedAt = Date.now();
    const tick = (): void => {
      if (closed || queue.connected) {
        announceTimer = null;
        return;
      }
      const announce: LeaderBroadcastMessage = { tabId, type: "tab-here" };
      broadcastChannel.postMessage(announce);
      const elapsed = Date.now() - announceStartedAt;
      announceTimer = setTimeout(
        tick,
        elapsed < ANNOUNCE_FAST_WINDOW_MS ? ANNOUNCE_FAST_INTERVAL_MS : ANNOUNCE_SLOW_INTERVAL_MS,
      );
    };
    tick();
  }

  // ── Channel handlers ─────────────────────────────────────────────────────────
  broadcastChannel.addEventListener("message", (event: MessageEvent) => {
    const message = parseLeaderMessage(event.data);
    if (message === null) {
      return;
    }
    if (isLeaderHereMessage(message)) {
      if (message.leaderEpoch === leaderEpoch) {
        return;
      }
      // A NEW leader exists. There is no reliable "old leader died" signal —
      // `leader-here` IS the signal. Fires in every tab including the new
      // leader's own.
      leaderEpoch = message.leaderEpoch;
      queue.enterHandover();
      setStatus({ queuedCount: queue.queuedCount, sinceMs: 0, state: "handover" });
      startAnnounceLoop();
      return;
    }
    if (isLeaderPausedMessage(message)) {
      // The CURRENT leader closed its resource on purpose (the Turso sign-in
      // wipe). Queue, but do NOT restart the announce loop: leadership has not
      // changed, so there is no new leader to find, and announcing would only
      // make the leader re-`connectTab` us mid-pause and flush into a closed
      // engine. `leader-resumed` is the resumption signal.
      if (message.leaderEpoch !== leaderEpoch) {
        return;
      }
      queue.enterPause();
      setStatus({
        queuedCount: queue.queuedCount,
        sinceMs: queue.sinceHandoverMs ?? 0,
        state: "handover",
      });
      return;
    }
    if (isLeaderResumedMessage(message)) {
      if (message.leaderEpoch !== leaderEpoch) {
        return;
      }
      setStatus({ isLeader, state: "connected" });
      void queue.connect();
      return;
    }
    if (isLeaderEventMessage(message)) {
      options.onEvent?.(message.event);
    }
    // `leader-failed` is informational: tabs deliberately stay in handover,
    // because another candidate (or the same worker on its next attempt) may
    // still win and open successfully.
  });

  tabChannel.addEventListener("message", (event: MessageEvent) => {
    const message = parseLeaderMessage(event.data);
    if (message === null) {
      return;
    }
    if (isConnectedMessage(message)) {
      leaderEpoch = message.leaderEpoch;
      leaderRequestDeadlinesMs = readRequestDeadlinesMs(message);
      stopAnnounceLoop();
      setStatus({ isLeader, state: "connected" });
      readyResolve?.();
      void queue.connect();
      return;
    }
    if (isRpcReturnMessage(message)) {
      queue.resolveInFlight(message.callId, message.result);
      return;
    }
    if (isRpcErrorMessage(message)) {
      queue.failInFlight(message.callId, deserializeLeaderError(message.error));
    }
  });

  // Set when our own worker reports that its relinquish finished — i.e. the host
  // ran `teardown()` and the resource's `close()` (including the guest WAL
  // checkpoint) resolved, or it was not leading and had nothing to tear down.
  // `close()` waits for this, bounded, before the caller may terminate the worker.
  let relinquishedByHost = false;
  let onRelinquished: (() => void) | null = null;

  // ── Worker handshake ─────────────────────────────────────────────────────────
  options.worker.addEventListener("message", (event: MessageEvent) => {
    const message = parseLeaderMessage(event.data);
    if (message === null) {
      return;
    }
    if (isRelinquishedMessage(message)) {
      relinquishedByHost = true;
      onRelinquished?.();
      return;
    }
    if (isWorkerHereMessage(message)) {
      options.worker.postMessage({
        // Forwarded verbatim to this tab's own host `open()`. Kept out of the
        // worker URL on purpose — it can carry an auth token, which a URL would
        // leak into DevTools' network panel.
        openContext: options.openContext,
        resourceId: options.resourceId,
        tabId,
        type: "host-init",
      });
      return;
    }
    if (isHostReadyMessage(message)) {
      hostReady = true;
      // Election is under way in our worker. Start announcing so an EXISTING
      // leader (in another tab) adopts us immediately rather than after our own
      // worker loses the race.
      startAnnounceLoop();
      return;
    }
    if (isLeaderNowMessage(message)) {
      isLeader = true;
      if (status.state === "connected") {
        setStatus({ isLeader: true, state: "connected" });
      }
      return;
    }
    if (isLeaderFailedDirectMessage(message)) {
      if (message.terminal === true) {
        // Our worker will not try again. If some other tab's leader already
        // adopted us, nothing changes; otherwise waiting out `ready()`'s deadline
        // could only end the same way, so fail it now with the real reason.
        hostGone = true;
        failReady(message.reason);
        return;
      }
      // Not terminal: this worker (or another tab) may still win and open.
      // Surface the reason so a screenshot artifact is diagnostic.
      if (!queue.connected) {
        setStatus({ reason: message.reason, state: "failed" });
      }
    }
  });

  // A worker whose script fails to evaluate (a bundling bug, a missing global)
  // never posts anything, so without these the tab would wait on it forever.
  // Before `host-ready` an error means the host never started: terminal. After
  // it, an uncaught error in some handler does not stop the host, and `ready()`'s
  // deadline covers anything it does break.
  const onWorkerError = (event: Event): void => {
    if (hostReady || closed) {
      return;
    }
    hostGone = true;
    failReady(`Leader worker failed to start: ${describeWorkerError(event)}`);
  };
  options.worker.addEventListener("error", onWorkerError);
  options.worker.addEventListener("messageerror", onWorkerError);

  // ── Tab-close lock ───────────────────────────────────────────────────────────
  // Acquired BEFORE any `tab-here` goes out — see the precondition in
  // `startAnnounceLoop` for why announcing first is a real (not theoretical) bug.
  let releaseTabCloseLock: (() => void) | null = null;
  const tabCloseLockAcquired = acquireWebLock(getTabCloseLockId(tabId), {
    deadline: TAB_CLOSE_LOCK_DEADLINE_MS,
  }).then((release) => {
    if (closed) {
      release();
      return;
    }
    releaseTabCloseLock = release;
    tabCloseLockHeld = true;
    // Release whatever asked to announce while the lock was still pending: the
    // worker handshake (`host-ready`) usually wins that race.
    if (announceRequestedBeforeLock) {
      announceRequestedBeforeLock = false;
      startAnnounceLoop();
    }
  });
  tabCloseLockAcquired.catch((error: unknown) => {
    // Without the lock this tab may never announce, so no leader can adopt it.
    failReady(
      `Could not take this tab's close lock: ${error instanceof Error ? error.message : String(error)}`,
    );
  });

  // ── Proactive relinquish (OPTIMIZATION ONLY) ─────────────────────────────────
  // The Web Lock auto-release on tab death remains the sole CORRECTNESS
  // guarantee; nothing in the state machine may depend on this having run.
  // `persisted === true` means bfcache — the page may come BACK, so relinquishing
  // there would give up leadership for a tab that then resumes and must re-elect.
  const onPageHide = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      return;
    }
    options.worker.postMessage({ type: "relinquish" });
  };
  if (typeof addEventListener === "function") {
    addEventListener("pagehide", onPageHide);
  }

  // Announce as soon as the lock lands, even if the worker handshake has not
  // reached `host-ready` yet: an EXISTING leader in another tab can adopt this
  // tab immediately, long before this tab's own worker loses the election. When
  // the lock is already held this is a no-op re-entry (the loop self-guards).
  void tabCloseLockAcquired.then(() => {
    if (!closed) {
      startAnnounceLoop();
    }
  });

  function nextCallId(): string {
    nextCallSeq += 1;
    return `${tabId}:c${nextCallSeq}`;
  }

  // Build the proxy by MUTATING a value already declared at the target type,
  // rather than casting `Object.fromEntries`'s index-signature result down to the
  // mapped type. The allowlist IS the Api's key set by construction, but
  // TypeScript cannot relate the two, and an assertion here would be a genuinely
  // unchecked narrowing rather than a documented one.
  const api: LeaderResourceClient<Api>["api"] = Object.create(null);
  for (const method of options.methods) {
    Reflect.set(api, method, async (...args: unknown[]): Promise<unknown> => {
      if (closed) {
        throw new LeaderUnavailableError("Leader resource client is closed");
      }
      return queue.request(method, args, nextCallId(), {
        resumesPausedLeader: resumeMethods.has(method),
      });
    });
  }

  return {
    api,
    close: async () => {
      if (closed) {
        return;
      }
      closed = true;
      stopAnnounceLoop();
      if (typeof removeEventListener === "function") {
        removeEventListener("pagehide", onPageHide);
      }
      // If this tab is the leader, relinquish so another tab can take over —
      // rather than "close and leave nobody in charge".
      options.worker.postMessage({ type: "relinquish" });
      // Give the host a bounded moment to finish that relinquish before the
      // caller terminates the worker. `closeClientAndWorker` calls
      // `workerHandle.dispose()` (→ `worker.terminate()`) the instant this
      // resolves, and terminate is immediate and unblockable — so without this
      // wait the host was routinely killed inside `teardown()`, truncating the
      // resource `close()` that makes buffered writes durable.
      //
      // Bounded, and deliberately never rejecting: correctness still rests on
      // the Web Lock auto-release, so a worker that is already dead (or is not
      // the leader and so never relinquishes) must not stall `close()`.
      if (!relinquishedByHost && !hostGone) {
        await boundedPromise(
          {
            deadline: RELINQUISH_DRAIN_TIMEOUT_MS,
            onDeadline: "resolve",
            operation: `leader resource "${options.resourceId}" relinquish`,
          },
          (resolve) => {
            onRelinquished = resolve;
            return () => {
              onRelinquished = null;
            };
          },
        );
      }
      options.worker.removeEventListener("error", onWorkerError);
      options.worker.removeEventListener("messageerror", onWorkerError);
      queue.destroy("Leader resource client is closed");
      readyReject?.(new LeaderUnavailableError("Leader resource client is closed"));
      await tabCloseLockAcquired.catch(() => {});
      releaseTabCloseLock?.();
      tabChannel.close();
      broadcastChannel.close();
    },
    get isLeader() {
      return isLeader;
    },
    ready: async () => readyPromise,
    get status() {
      return status;
    },
    // Exposed so a consumer can recognise (and ignore) the leader's echo of its
    // OWN calls — see `LeaderResourceClient.tabId`.
    tabId,
  };
}
